/**
 * Story 062 — Add-component-model modal (`/component-models?new=1`).
 *
 * - Required: Component Model Code, Name, Category, Component Group
 *   (AC14).
 * - Save disabled until all four required fields are non-empty.
 * - Specifications sub-form seeds defaults when the user picks a
 *   Component Group (AC15). Switching groups fires a confirmation
 *   modal that, on Confirm, REPLACES the rows with the new group's
 *   defaults (AC16).
 *
 * Submit body (AC17):
 *   { assetComponentModelCode, name: {en}, category, componentGroupId,
 *     manufacturer?, manufacturerAbbreviation?, specifications?: [...],
 *     externalIds?: [...], description?: {en} }
 * — NO id, NO scope (server-derived), NO audit (BE-assigned).
 *
 * 409 conflict on `assetComponentModelCode` surfaces inline on the
 * Code field (AC18b — `aria-invalid="true"` + helper text).
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../../admin/shared/ConfirmDialog.js";
import { defaultsForGroupCode, useCanEditResourceFor } from "@aeriosiq/ui";
import {
  useCreateComponentModelMutation,
  useCreateGlobalComponentModelMutation,
  useListComponentGroupsQuery,
  type AssetComponentModelExternalId,
  type AssetComponentModelSpecification,
  type ComponentGroup,
  type CreateAssetComponentModelBody,
} from "../queries/index.js";
import { ExternalIdsField } from "../../sites/shared/ExternalIdsField.js";
import { SpecificationsField } from "../shared/SpecificationsField.js";

interface AddComponentModelFormValues {
  assetComponentModelCode: string;
  name: string;
  category: string;
  componentGroupId: string;
  manufacturer: string;
  manufacturerAbbreviation: string;
  specifications: AssetComponentModelSpecification[];
  externalIds: AssetComponentModelExternalId[];
  description: string;
}

const EMPTY: AddComponentModelFormValues = {
  assetComponentModelCode: "",
  name: "",
  category: "",
  componentGroupId: "",
  manufacturer: "",
  manufacturerAbbreviation: "",
  specifications: [],
  externalIds: [],
  description: "",
};

function dropEmptySpecifications(
  rows: AssetComponentModelSpecification[],
): AssetComponentModelSpecification[] {
  return rows
    .map((r) => ({
      parameter: r.parameter.trim(),
      unit: r.unit.trim(),
      value: r.value.trim(),
    }))
    .filter((r) => r.parameter.length > 0);
}

function dropEmptyExternalIds(
  arr: AssetComponentModelExternalId[],
): AssetComponentModelExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildCreateBody(
  v: AddComponentModelFormValues,
): CreateAssetComponentModelBody {
  const body: CreateAssetComponentModelBody = {
    assetComponentModelCode: v.assetComponentModelCode.trim(),
    name: { en: v.name.trim() },
    category: v.category.trim(),
    componentGroupId: v.componentGroupId,
  };
  if (v.manufacturer.trim().length > 0) {
    body.manufacturer = v.manufacturer.trim();
  }
  if (v.manufacturerAbbreviation.trim().length > 0) {
    body.manufacturerAbbreviation = v.manufacturerAbbreviation.trim();
  }
  const specs = dropEmptySpecifications(v.specifications);
  if (specs.length > 0) body.specifications = specs;
  const ids = dropEmptyExternalIds(v.externalIds);
  if (ids.length > 0) body.externalIds = ids;
  if (v.description.trim().length > 0) {
    body.description = { en: v.description.trim() };
  }
  return body;
}

export interface AddComponentModelDialogProps {
  open: boolean;
  onClose: () => void;
  /** Optional category suggestions sourced from the list rollup. */
  categorySuggestions?: readonly string[];
  /** Optional manufacturer suggestions sourced from the list rollup. */
  manufacturerSuggestions?: readonly string[];
  /** SB2-W-07 — scope of the new row. Defaults to `'tenant'`. When
   *  `'global'`, sends `tenantId: null` and gates on
   *  `component_model.create_global` (platform_admin only). */
  scope?: "tenant" | "global";
}

export function AddComponentModelDialog({
  open,
  onClose,
  categorySuggestions = [],
  manufacturerSuggestions = [],
  scope = "tenant",
}: AddComponentModelDialogProps): ReactElement {
  const { toast } = useToast();
  const createTenant = useCreateComponentModelMutation();
  const createGlobal = useCreateGlobalComponentModelMutation();
  const create = scope === "global" ? createGlobal : createTenant;
  // SB2-CC-04 — defence-in-depth banner.
  const permissionKey =
    scope === "global"
      ? "component_model.create_global"
      : "component_model.create_tenant";
  const canCreate = useCanEditResourceFor(permissionKey);
  const componentGroupsQuery = useListComponentGroupsQuery();

  const componentGroups: readonly ComponentGroup[] = useMemo(
    () => componentGroupsQuery.data?.items ?? [],
    [componentGroupsQuery.data?.items],
  );
  const componentGroupById = useMemo(() => {
    const m = new Map<string, ComponentGroup>();
    for (const g of componentGroups) m.set(g.id, g);
    return m;
  }, [componentGroups]);

  const { control, register, handleSubmit, reset, watch, setValue, getValues } =
    useForm<AddComponentModelFormValues>({
      defaultValues: EMPTY,
      mode: "onChange",
    });

  const [codeError, setCodeError] = useState<string | null>(null);
  const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      reset(EMPTY);
      setCodeError(null);
      setPendingGroupId(null);
    }
  }, [open, reset]);

  const code = watch("assetComponentModelCode");
  const name = watch("name");
  const category = watch("category");
  const componentGroupId = watch("componentGroupId");
  const specifications = watch("specifications");

  const canSave =
    code.trim().length > 0 &&
    name.trim().length > 0 &&
    category.trim().length > 0 &&
    componentGroupId.length > 0 &&
    !create.isPending;

  // ── Component-Group switch flow (AC15 / AC16) ─────────────────────────
  // Setting the group from empty → first pick seeds defaults silently.
  // Setting it when prior specs exist fires the confirmation dialog.
  // The dialog stages the next group id in `pendingGroupId`; only on
  // Confirm do we replace specs + commit the group id.
  function seedSpecsFromGroupId(nextGroupId: string): void {
    const group = componentGroupById.get(nextGroupId);
    const defaults = defaultsForGroupCode(group?.groupCode);
    setValue(
      "specifications",
      defaults.map((d) => ({ ...d })),
      {
        shouldDirty: true,
        shouldValidate: false,
      },
    );
  }

  function handleComponentGroupChange(nextGroupId: string | null): void {
    if (!nextGroupId) {
      setValue("componentGroupId", "", { shouldDirty: true });
      return;
    }
    if (nextGroupId === componentGroupId) return;
    const hasExistingSpecs = getValues("specifications").length > 0;
    if (hasExistingSpecs && componentGroupId.length > 0) {
      // Defer the swap; show the confirm dialog.
      setPendingGroupId(nextGroupId);
      return;
    }
    setValue("componentGroupId", nextGroupId, {
      shouldDirty: true,
      shouldValidate: true,
    });
    seedSpecsFromGroupId(nextGroupId);
  }

  function confirmGroupSwap(): void {
    if (!pendingGroupId) return;
    setValue("componentGroupId", pendingGroupId, {
      shouldDirty: true,
      shouldValidate: true,
    });
    seedSpecsFromGroupId(pendingGroupId);
    setPendingGroupId(null);
  }
  function cancelGroupSwap(): void {
    setPendingGroupId(null);
  }

  async function onSubmit(values: AddComponentModelFormValues): Promise<void> {
    setCodeError(null);
    try {
      const body = buildCreateBody(values);
      await create.mutateAsync(body);
      toast(
        scope === "global"
          ? "Global component model added."
          : "Component model added.",
      );
      onClose();
    } catch (err) {
      const errBody = (err as { body?: { code?: string; detail?: string } })
        .body;
      const status = (err as { status?: number }).status;
      if (status === 409) {
        setCodeError(
          errBody?.detail ?? "asset_component_model_code already exists",
        );
        return;
      }
      if (errBody?.detail?.startsWith("forbidden_field")) {
        toast("Cannot set this field — it's managed by the system.", "error");
        return;
      }
      toast("Couldn't add component model. Try again.", "error");
    }
  }

  // Track previous specs to detect "anything left to lose" for AC15.
  // (Mainly here so a future test that pre-fills specs sees the
  // expected confirm — current empty-array case is handled inline.)
  const specsLengthRef = useRef(specifications.length);
  specsLengthRef.current = specifications.length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="add-component-model-heading"
      >
        <DialogTitle id="add-component-model-heading">
          {scope === "global"
            ? "Add global component model"
            : "Add component model"}
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent>
            {!canCreate ? (
              <Alert
                severity="info"
                variant="outlined"
                role="status"
                aria-live="polite"
                data-testid="add-modal-permission-banner"
                sx={{ mb: 2 }}
              >
                Your role does not allow adding a component model.
              </Alert>
            ) : null}
            <Stack spacing={2}>
              <TextField
                {...register("assetComponentModelCode", {
                  required: true,
                  minLength: 1,
                  maxLength: 50,
                })}
                label="Component Model Code"
                required
                error={codeError !== null}
                helperText={codeError ?? " "}
                onChange={(e) => {
                  setCodeError(null);
                  // Don't fight RHF's register — pass through.
                  void register("assetComponentModelCode").onChange(e);
                }}
                slotProps={{
                  htmlInput: {
                    "aria-label": "Component Model Code",
                    "aria-required": true,
                    "aria-invalid": codeError !== null ? true : undefined,
                    maxLength: 50,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("name", { required: true, minLength: 1 })}
                label="Name"
                required
                // Modals legitimately autofocus their first editable
                // field — same pattern as Sites Add.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                slotProps={{
                  htmlInput: {
                    "aria-label": "Name",
                    "aria-required": true,
                    maxLength: 255,
                  },
                }}
                fullWidth
              />
              <Controller
                name="category"
                control={control}
                rules={{ required: true, minLength: 1 }}
                render={({ field }) => (
                  <Autocomplete<string, false, false, true>
                    freeSolo
                    options={[...categorySuggestions]}
                    value={field.value}
                    onChange={(_e, next) => {
                      field.onChange(next ?? "");
                    }}
                    onInputChange={(_e, next) => {
                      field.onChange(next);
                    }}
                    renderInput={(params) => {
                      /* eslint-disable @typescript-eslint/no-explicit-any */
                      const passThrough: any = params;
                      return (
                        <TextField
                          {...passThrough}
                          label="Category"
                          required
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              "aria-label": "Category",
                              "aria-required": true,
                              maxLength: 100,
                            },
                          }}
                          fullWidth
                        />
                      );
                      /* eslint-enable */
                    }}
                  />
                )}
              />
              <Controller
                name="componentGroupId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => {
                  const selected =
                    componentGroups.find((g) => g.id === field.value) ?? null;
                  return (
                    <Autocomplete<ComponentGroup>
                      options={[...componentGroups]}
                      value={selected}
                      getOptionLabel={(o) => o.name.en ?? o.groupCode}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      onChange={(_e, next) => {
                        handleComponentGroupChange(next ? next.id : null);
                      }}
                      renderInput={(params) => {
                        /* eslint-disable @typescript-eslint/no-explicit-any */
                        const passThrough: any = params;
                        return (
                          <TextField
                            {...passThrough}
                            label="Component Group"
                            required
                            slotProps={{
                              htmlInput: {
                                ...params.inputProps,
                                "aria-label": "Component Group",
                                "aria-required": true,
                              },
                            }}
                            fullWidth
                          />
                        );
                        /* eslint-enable */
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="manufacturer"
                control={control}
                render={({ field }) => (
                  <Autocomplete<string, false, false, true>
                    freeSolo
                    options={[...manufacturerSuggestions]}
                    value={field.value}
                    onChange={(_e, next) => {
                      field.onChange(next ?? "");
                    }}
                    onInputChange={(_e, next) => {
                      field.onChange(next);
                    }}
                    renderInput={(params) => {
                      /* eslint-disable @typescript-eslint/no-explicit-any */
                      const passThrough: any = params;
                      return (
                        <TextField
                          {...passThrough}
                          label="Manufacturer"
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              "aria-label": "Manufacturer",
                              maxLength: 100,
                            },
                          }}
                          fullWidth
                        />
                      );
                      /* eslint-enable */
                    }}
                  />
                )}
              />
              <TextField
                {...register("manufacturerAbbreviation")}
                label="Manufacturer abbreviation"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Manufacturer abbreviation",
                    maxLength: 20,
                  },
                }}
                fullWidth
              />
              <SpecificationsField control={control} register={register} />
              <ExternalIdsField control={control} register={register} />
              <TextField
                {...register("description")}
                label="Description (EN)"
                multiline
                rows={3}
                slotProps={{
                  htmlInput: {
                    "aria-label": "Description",
                    maxLength: 2000,
                  },
                }}
                fullWidth
              />
              {componentGroupsQuery.isError ? (
                <Alert severity="warning" variant="outlined">
                  Couldn&apos;t load Component Groups — try again later.
                </Alert>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            {canCreate ? (
              <Button
                type="submit"
                variant="contained"
                disabled={!canSave}
                startIcon={
                  create.isPending ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                Save
              </Button>
            ) : null}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Component-Group swap confirmation (AC16). Title MUST contain
          "Confirm" so the dialog's accessible name matches
          `getByRole("dialog", { name: /confirm/i })`. */}
      <ConfirmDialog
        open={pendingGroupId !== null}
        title="Confirm: Switch Component Group?"
        description={
          <Box>
            Switching component group discards your current specifications. The
            parameter rows will be replaced with the defaults for the new group.
          </Box>
        }
        confirmLabel="Confirm"
        onConfirm={confirmGroupSwap}
        onCancel={cancelGroupSwap}
      />
    </>
  );
}
